import type {
  CustomerNote,
  NewCustomerNoteInput,
  UpdateCustomerNoteInput,
} from '../domain/models'
import type {
  AuthStorePort,
  ClockPort,
  CustomerNotesRepositoryPort,
  IdGeneratorPort,
} from '../ports'

/**
 * The customer's own notes thread — the twin of the service note feed in
 * `callouts.ts`, minus the service.
 *
 * Two differences follow from having no parent service:
 *
 *  - There is no `touchService`. A customer has no lock status and no
 *    `updatedAt` worth bumping from a note, so these commands write one record.
 *  - There is no lock to work around. Service notes stay editable *despite*
 *    the service being locked; here nothing was ever frozen in the first place.
 *
 * Everything else is deliberately identical — same revision trail, same soft
 * delete, same failed-upload rollback — so the same feed components render both.
 */

export function appendCustomerNote(
  customerId: string,
  input: NewCustomerNoteInput,
  deps: {
    authStore: AuthStorePort
    clock: ClockPort
    idGenerator: IdGeneratorPort
    customerNotesRepo: CustomerNotesRepositoryPort
  },
): CustomerNote {
  const { note } = appendCustomerNoteWithTransaction(customerId, input, deps)
  return note
}

/**
 * Append a note and hand back the write in flight.
 *
 * Photo notes need this: the caller waits on the transaction so an upload that
 * fails can be rolled back with {@link discardCustomerNote} before it leaves a
 * tombstone.
 */
export function appendCustomerNoteWithTransaction(
  customerId: string,
  input: NewCustomerNoteInput,
  deps: {
    authStore: AuthStorePort
    clock: ClockPort
    idGenerator: IdGeneratorPort
    customerNotesRepo: CustomerNotesRepositoryPort
  },
): {
  note: CustomerNote
  transaction: ReturnType<CustomerNotesRepositoryPort['insert']>
} {
  const timestamp = deps.clock.now()
  const currentUser = deps.authStore.user
  if (!currentUser) {
    throw new Error('Not authenticated')
  }

  const note = {
    id: deps.idGenerator.generateId(),
    customerId,
    createdAt: timestamp,
    updatedAt: timestamp,
    ...input,
    // Forced last: the author is who is signed in, never what the caller says.
    authorId: currentUser.id,
  } as CustomerNote

  const transaction = deps.customerNotesRepo.insert(note)

  return { note, transaction }
}

/**
 * Edit a note, keeping what it said before.
 *
 * The superseded text/caption/photo is pushed onto `revisions` before the new
 * value lands, matching `updateLog`.
 */
export function updateCustomerNote(
  customerId: string,
  noteId: string,
  patch: UpdateCustomerNoteInput,
  deps: {
    authStore: AuthStorePort
    clock: ClockPort
    customerNotesRepo: CustomerNotesRepositoryPort
  },
) {
  const timestamp = deps.clock.now()
  const currentUser = deps.authStore.user
  if (!currentUser) {
    throw new Error('Not authenticated')
  }

  deps.customerNotesRepo.update(noteId, (draft) => {
    if (draft.customerId !== customerId) return

    const textChanged = patch.text !== undefined && patch.text !== draft.text
    const captionChanged =
      patch.caption !== undefined && patch.caption !== draft.caption
    const photoChanged =
      Boolean(patch.photoFile || patch.photoFiles?.length) &&
      Boolean(draft.photo || draft.photos?.length)

    if (textChanged || captionChanged || photoChanged) {
      draft.revisions = [
        ...(draft.revisions ?? []),
        {
          text: draft.text,
          caption: draft.caption,
          photo: draft.photo,
          // The whole group this version showed — revisions are recorded on
          // the group, never per photo.
          photos: (draft.photos?.length ?? 0) > 1 ? draft.photos : undefined,
          at: timestamp,
          authorId: currentUser.id,
        },
      ]
    }

    Object.assign(draft, patch)
    if (photoChanged) {
      // See updateLog: the server names replacement files, so the group list
      // must not pin the superseded set.
      draft.photos = undefined
      draft.photoUrls = patch.photoUrl ? [patch.photoUrl] : undefined
    }
    draft.updatedAt = timestamp
  })
}

/**
 * Soft-delete a note: it drops out of the thread but its content is retained
 * and rendered as a tombstone.
 */
export function deleteCustomerNote(
  customerId: string,
  noteId: string,
  deps: {
    authStore: AuthStorePort
    clock: ClockPort
    customerNotesRepo: CustomerNotesRepositoryPort
  },
) {
  const timestamp = deps.clock.now()
  const currentUser = deps.authStore.user
  if (!currentUser) {
    throw new Error('Not authenticated')
  }

  deps.customerNotesRepo.update(noteId, (draft) => {
    if (draft.customerId !== customerId) return
    if (draft.deletedAt) return
    draft.deletedAt = timestamp
    draft.deletedBy = currentUser.id
    draft.updatedAt = timestamp
  })
}

/**
 * Hard-delete a note.
 *
 * Reserved for rolling back a note whose photo upload failed — it never became
 * part of the thread, so it should leave no tombstone. Everything a user
 * deletes goes through {@link deleteCustomerNote} instead.
 */
export function discardCustomerNote(
  noteId: string,
  deps: { customerNotesRepo: CustomerNotesRepositoryPort },
) {
  deps.customerNotesRepo.delete(noteId)
}
